<script lang="ts" setup>
import { ref, watch, computed, toRef } from "vue";
import PreviewSubscriberPicker from "@/views/shared/components/PreviewSubscriberPicker.vue";
import { useUpdatePreviewSubscribers } from "@/queries/products";
import type { VersionDetails } from "@/types/version-types";

interface Props {
  show: boolean;
  version: VersionDetails | null;
  /** Product the version belongs to — required for the Preview subscriber picker. */
  productId: string;
}

interface Emits {
  (e: "close"): void;
  (e: "publish", versionId: string): void;
  (e: "archive", versionId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isPreview = computed(() => props.version?.status === "Preview");

const previewSelection = ref<string[]>([]);
const productIdForMutation = toRef(props, "productId");
const {
  updatePreviewSubscribersAsync,
  updatePreviewSubscribersPending,
} = useUpdatePreviewSubscribers(productIdForMutation);

async function savePreviewSubscribers() {
  if (!props.version) return;
  await updatePreviewSubscribersAsync({
    versionId: props.version.id,
    subscriberOrgIds: previewSelection.value,
  });
}

function closeModal() {
  emit("close");
}

function publishVersion() {
  if (props.version) {
    emit("publish", props.version.id);
  }
}

function archiveVersion() {
  if (props.version) {
    emit("archive", props.version.id);
  }
}

const canPublish = computed(
  () => props.version && props.version.status !== "Published",
);
const canArchive = computed(() => props.version?.status === "Preview");

function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "published":
      return "bg-primary";
    case "preview":
      return "bg-info";
    case "draft":
      return "bg-warning";
    case "archived":
      return "bg-secondary";
    default:
      return "bg-warning";
  }
}

function formatDate(dateString: string): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      previewSelection.value = [];
    }
  },
);
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show, 'd-block': show }"
    tabindex="-1"
    :style="{ display: show ? 'block' : 'none' }"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <div class="d-flex align-items-center gap-3">
            <h2 class="modal-title mb-0">
              <i class="bi bi-code-square text-primary me-2"></i>
              Version {{ version?.version }}
            </h2>
            <span
              v-if="version"
              class="badge"
              :class="getStatusClass(version.status)"
            >
              {{ version.status }}
            </span>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body p-0" v-if="version">
          <div class="p-4">
            <!-- Preview subscriber picker — the primary action when status is Preview -->
            <div v-if="isPreview" class="mb-4 pb-4 border-bottom">
              <h3 class="mb-3">Preview Subscribers</h3>
              <p class="text-muted small">
                Pick which subscriber orgs see this Preview. Removed orgs immediately revert to
                the current Published version; added orgs are synced onto the Preview.
                Customizations are preserved either way.
              </p>
              <PreviewSubscriberPicker
                :product-id="productId"
                preselect-current
                v-model="previewSelection"
              />
              <p
                v-if="previewSelection.length === 0"
                class="text-danger small mt-2 mb-0"
              >
                Select at least one subscriber org. To clear the Preview entirely, archive it.
              </p>
              <div class="mt-3 d-flex justify-content-end">
                <button
                  type="button"
                  class="bb-btn-primary-cta"
                  :disabled="updatePreviewSubscribersPending || previewSelection.length === 0"
                  @click="savePreviewSubscribers"
                >
                  <span
                    v-if="updatePreviewSubscribersPending"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="bi bi-save me-1"></i>
                  {{ updatePreviewSubscribersPending ? "Saving…" : "Save Subscribers" }}
                </button>
              </div>
            </div>

            <div class="row g-4">
              <div class="col-lg-8">
                <h3 class="mb-3">Description</h3>
                <p class="mb-4">{{ version.description }}</p>

                <div v-if="version.releaseNotes">
                  <h3 class="mb-3">Release Notes</h3>
                  <div class="bg-light-subtle p-3 rounded">
                    <pre
                      class="mb-0"
                      style="white-space: pre-wrap; font-family: inherit"
                      >{{ version.releaseNotes }}</pre
                    >
                  </div>
                </div>

                <!-- Workflow Version Breakdown -->
                <div v-if="version.workflowVersions?.length" class="mt-4">
                  <h3 class="mb-3">Workflows</h3>
                  <div class="list-group list-group-flush">
                    <div
                      v-for="wf in version.workflowVersions"
                      :key="wf.name"
                      class="list-group-item border-0 px-0 py-2 bg-transparent"
                    >
                      <div class="d-flex align-items-center">
                        <i class="bi bi-diagram-3 text-muted me-2"></i>
                        <strong>{{ wf.name }}</strong>
                        <span class="ms-2 text-muted">{{ wf.currentVersion }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Connection slots snapshotted by this version -->
                <div v-if="version.connections?.length" class="mt-4">
                  <h3 class="mb-3">Connections</h3>
                  <div class="list-group list-group-flush">
                    <div
                      v-for="conn in version.connections"
                      :key="conn.name"
                      class="list-group-item border-0 px-0 py-2 bg-transparent"
                    >
                      <div class="d-flex align-items-center">
                        <i class="bi bi-plug text-muted me-2"></i>
                        <strong>{{ conn.name }}</strong>
                        <span class="ms-2 text-muted">{{ conn.type }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Datastores snapshotted by this version -->
                <div v-if="version.datastoreNames?.length" class="mt-4">
                  <h3 class="mb-3">Datastores</h3>
                  <div class="list-group list-group-flush">
                    <div
                      v-for="name in version.datastoreNames"
                      :key="name"
                      class="list-group-item border-0 px-0 py-2 bg-transparent"
                    >
                      <div class="d-flex align-items-center">
                        <i class="bi bi-database text-muted me-2"></i>
                        <strong>{{ name }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-4">
                <div class="card">
                  <div class="card-header">
                    <h3 class="card-title mb-0">Version Information</h3>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <small class="text-muted d-block">Created</small>
                      <strong>{{ formatDate(version.createdDate) }}</strong>
                    </div>

                    <div v-if="version.publishedDate" class="mb-3">
                      <small class="text-muted d-block">Published</small>
                      <strong>{{ formatDate(version.publishedDate) }}</strong>
                    </div>

                    <div v-if="version.archivedDate" class="mb-3">
                      <small class="text-muted d-block">Archived</small>
                      <strong>{{ formatDate(version.archivedDate) }}</strong>
                    </div>

                    <div class="mb-3">
                      <small class="text-muted d-block">Workflows</small>
                      <strong>{{ version.workflowVersions?.length ?? 0 }}</strong>
                    </div>

                    <div class="mb-3">
                      <small class="text-muted d-block">Subscribers</small>
                      <strong>{{ version.subscribers }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="closeModal">
            Close
          </button>

          <div class="d-flex gap-2" v-if="version">
            <button
              v-if="canPublish"
              type="button"
              class="bb-btn-primary-cta"
              @click="publishVersion"
            >
              <i class="bi bi-rocket me-1"></i>Publish
            </button>
            <button
              v-if="canArchive"
              type="button"
              class="bb-btn-danger"
              @click="archiveVersion"
            >
              <i class="bi bi-archive me-1"></i>Archive
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="show" class="modal-backdrop fade show" @click="closeModal"></div>
</template>

<style scoped lang="scss">
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

pre {
  font-size: 14px;
  line-height: 1.5;
}
</style>
